// Angular
import { Component, DestroyRef, inject, signal } from '@angular/core';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material / CDK
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

// Services
import { AuthService } from '@guard/auth.service';

// Defs
import { Breadcrumb } from './defs';

// Constants
import { AccessRight } from '@constants/enum';

// Pipes
import { AnyRightsPipe } from '@utils/any-rights.pipe';

@Component({
  selector: 'app-navigation',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatButtonModule,
    MatTooltipModule,
    AnyRightsPipe,
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
})
export class NavigationComponent {
  public router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  public oauthService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private breakpointObserver = inject(BreakpointObserver);

  constructor() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.generateBreadCrumbs();
    });

    this.breakpointObserver
      .observe([`(max-width: ${this.smallerScreen}px)`])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.expanded.set(!result.matches);
      });
  }

  readonly expanded = signal(true);
  readonly smallerScreen = 1450;
  readonly crumbs = signal<Breadcrumb[]>([]);
  readonly resizeActive = signal(false);

  readonly accessRight = AccessRight;

  logout() {
    this.oauthService.logOut();
  }

  generateBreadCrumbs() {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const breadcrumb = route.snapshot.data['breadcrumb'];
    const type = route.snapshot.params['type'];
    const integrationType = route.snapshot.params['integrationType'];

    if (breadcrumb) {
      const displayText = breadcrumb
        .replace(':type', type)
        .replace(':integrationType', integrationType);
      const parts = displayText.split('/');

      this.crumbs.set(
        parts.map((part, index) => ({
          text: part,
          link:
            '/' +
            parts
              .slice(0, index + 1)
              .join('/')
              .toLowerCase()
              .replace(/\s+/g, '-'),
        })),
      );
    }
  }

  resize() {
    this.expanded.update((value) => !value);
  }
}
